(() => {
  const ROOT_SELECTOR = "[data-griffe-forms]";
  const initialized = new WeakSet();

  function initShapeExplorer(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    const isFrench = document.documentElement.lang.toLowerCase().startsWith('fr');

    const stages = {
      almond: root.querySelector('[data-shape-stage="almond"]'),
      stiletto: root.querySelector('[data-shape-stage="stiletto"]'),
    };
    const almondArt = root.querySelector('[data-shape-art="almond"]');
    const stilettoArt = stages.stiletto?.querySelector("[data-griffe-art]");
    const buttons = [...root.querySelectorAll("[data-shape-target]")];
    const status = root.querySelector("[data-shape-status]");
    const story = root.querySelector("[data-shape-story]");
    const storyIndex = root.querySelector("[data-shape-story-index]");
    const storyCopy = root.querySelector("[data-shape-story-copy]");
    const backToHero = root.querySelector('a[href="#gj-stage-almond"]');
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (!stages.almond || !stages.stiletto || !almondArt || !stilettoArt) return;

    const shapeOrder = ["round", "almond", "square", "coffin", "stiletto"];
    const carouselDelay = 2500;
    let carouselTimer = 0;
    let carouselVisible = false;
    let carouselDestroyed = false;
    let activeShape = "round";
    let switching = false;
    let queuedShape = null;
    let queuedSequential = false;
    let frame = 0;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let infographyTimer = 0;
    let morphCopyTimer = 0;
    let morphGeneration = 0;
    let morphTargetShape = null;
    let storyTimer = 0;
    let scrollFrame = 0;
    let scrollAnimationFrame = 0;

    almondArt.querySelectorAll(".gj-art__annotation > path").forEach((path) => {
      path.setAttribute("pathLength", "1");
    });

    const tokenizePath = (path) => path.match(/[a-zA-Z]|[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/gi) || [];
    const isCommand = (token) => /^[a-zA-Z]$/.test(token);
    const formatNumber = (number) => Number(number.toFixed(3));

    const collectMorphPaths = (art) => [
      art.querySelector("clipPath path"),
      ...art.querySelectorAll(".gj-nail-body > path"),
      ...art.querySelectorAll(".gj-layer--wire .gj-draw"),
      ...art.querySelectorAll(".gj-art__annotation > path"),
      ...art.querySelectorAll(".gj-art__profile .gj-draw"),
    ].filter(Boolean);

    const sourcePaths = collectMorphPaths(almondArt);
    const sourceTexts = [...almondArt.querySelectorAll(".gj-art__annotation text, .gj-art__profile text, .gj-art__index text")];
    const sourceNodes = [...almondArt.querySelectorAll(".gj-art__annotation circle, .gj-art__pulse")];

    const captureState = (art) => ({
      paths: collectMorphPaths(art).map((path) => path.getAttribute("d") || ""),
      texts: [...art.querySelectorAll(".gj-art__annotation text, .gj-art__profile text, .gj-art__index text")].map((node) => ({
        value: node.textContent || "",
        x: Number.parseFloat(node.getAttribute("x")),
        y: Number.parseFloat(node.getAttribute("y")),
      })),
      nodes: [...art.querySelectorAll(".gj-art__annotation circle, .gj-art__pulse")].map((node) => ({
        cx: Number.parseFloat(node.getAttribute("cx")),
        cy: Number.parseFloat(node.getAttribute("cy")),
        r: Number.parseFloat(node.getAttribute("r")),
      })),
    });

    const extendState = (base, overrides = {}) => ({
      paths: base.paths.map((value, index) => overrides.paths?.[index] || value),
      texts: base.texts.map((value, index) => ({ ...value, ...(overrides.texts?.[index] || {}) })),
      nodes: base.nodes.map((value, index) => ({ ...value, ...(overrides.nodes?.[index] || {}) })),
    });

    const almondRawState = captureState(almondArt);
    const almondState = extendState(almondRawState, {
      texts: {
        6: { value: "SIGNATURE 02. ALMOND" },
        8: { value: "SIGNATURE 02. ALMOND · STRUCTURED APEX" },
      },
    });
    const stilettoRawState = captureState(stilettoArt);
    const stilettoState = extendState(stilettoRawState, {
      paths: {
        0: "M395 54C365 105 327 184 299 283C271 382 254 498 256 548C261 617 294 654 350 669C379 677 411 677 440 669C496 654 529 617 534 548C536 498 519 382 491 283C463 184 425 105 395 54C395 54 395 54 395 54Z",
        1: "M395 54C365 105 327 184 299 283C271 382 254 498 256 548C261 617 294 654 350 669C379 677 411 677 440 669C496 654 529 617 534 548C536 498 519 382 491 283C463 184 425 105 395 54C395 54 395 54 395 54Z",
        2: "M395 54C365 105 327 184 299 283C271 382 254 498 256 548C261 617 294 654 350 669C366 674 382 677 395 677C357 637 347 566 356 449C366 307 381 160 395 54Z",
        3: "M377 111C345 181 318 277 298 388C284 484 284 575 324 628",
        4: "M414 108C448 181 476 285 493 397C506 494 502 575 467 634",
        5: "M435 168C465 274 480 390 480 497",
        6: "M395 54C365 105 327 184 299 283C271 382 254 498 256 548C261 617 294 654 350 669C379 677 411 677 440 669C496 654 529 617 534 548C536 498 519 382 491 283C463 184 425 105 395 54C395 54 395 54 395 54Z",
        7: "M395 88C371 132 342 202 320 292C297 388 284 482 298 539C303 599 328 629 366 642C384 648 405 648 424 642C462 629 487 599 492 539C506 482 493 388 470 292C448 202 419 132 395 88C395 88 395 88 395 88Z",
        8: "M395 48C393 224 391 475 395 677",
        9: "M274 405Q395 359 516 405M259 501Q395 458 537 496",
        10: "M315 638Q395 595 475 638",
        12: "M475 638H646v18",
      },
      texts: {
        2: { y: 656 },
        3: { y: 670 },
        6: { value: "SIGNATURE 05. STILETTO", y: 656 },
        8: { value: "SIGNATURE 05. STILETTO · STRUCTURED TIP" },
      },
      nodes: {
        1: { cx: 475, cy: 638 },
        3: { cx: 475, cy: 638 },
      },
    });

    const coffinState = extendState(almondState, {
      paths: {
        0: "M320 108C315 158 306 215 294 287C282 371 274 463 270 548C275 617 305 654 355 669C381 677 409 677 435 669C485 654 515 617 520 548C516 463 508 371 496 287C484 215 475 158 470 108C426 102 364 102 320 108Z",
        1: "M320 108C315 158 306 215 294 287C282 371 274 463 270 548C275 617 305 654 355 669C381 677 409 677 435 669C485 654 515 617 520 548C516 463 508 371 496 287C484 215 475 158 470 108C426 102 364 102 320 108Z",
        2: "M320 108C315 158 306 215 294 287C282 371 274 463 270 548C275 617 305 654 355 669C369 674 383 677 395 677C360 637 351 569 359 445C366 300 339 190 320 108Z",
        3: "M339 145C326 214 312 314 303 421C296 521 303 603 336 636",
        4: "M455 137C470 219 483 316 491 428C498 526 487 603 459 640",
        5: "M451 175C470 270 479 369 480 482",
        6: "M320 108C315 158 306 215 294 287C282 371 274 463 270 548C275 617 305 654 355 669C381 677 409 677 435 669C485 654 515 617 520 548C516 463 508 371 496 287C484 215 475 158 470 108C426 102 364 102 320 108Z",
        7: "M340 133C335 179 328 232 318 299C310 381 306 463 308 539C312 599 335 629 369 642C385 648 404 648 421 642C455 629 478 599 482 539C484 463 480 381 472 299C462 232 455 179 450 133C418 128 372 128 340 133Z",
        8: "M395 102C393 242 391 480 395 677",
        9: "M294 390Q394 348 496 390M276 501Q394 462 514 501",
        10: "M324 638Q395 595 466 638",
        11: "M487 307H584",
        12: "M466 638H646v18",
        13: "M318 89H472",
        14: "M674 462Q713 432 752 462",
        15: "M686 462Q713 445 740 462",
      },
      texts: {
        0: { value: "REFINED FOR ELEGANCE" },
        1: { value: "TAPERED SIDES" },
        2: { value: "PRECISE FIT" },
        3: { value: "CUTICLE CURVE" },
        4: { value: "REFINED LINES" },
        5: { value: "BORD LIBRE DROIT" },
        6: { value: "SIGNATURE 04. COFFIN" },
        7: { value: "REFINED FOR ELEGANCE", y: 57 },
        8: { value: "SIGNATURE 04. COFFIN · TAPERED SIDES", y: 76 },
      },
      nodes: {
        0: { cx: 487, cy: 307 },
        1: { cx: 466, cy: 638 },
        2: { cx: 487, cy: 307 },
        3: { cx: 466, cy: 638 },
      },
    });

    const squareState = extendState(almondState, {
      paths: {
        0: "M280 108C280 200 280 340 280 468C280 500 280 530 280 548C283 617 311 654 357 669C381 677 409 677 433 669C479 654 507 617 510 548C510 530 510 500 510 468C510 340 510 200 510 108C446 102 344 102 280 108Z",
        1: "M280 108C280 200 280 340 280 468C280 500 280 530 280 548C283 617 311 654 357 669C381 677 409 677 433 669C479 654 507 617 510 548C510 530 510 500 510 468C510 340 510 200 510 108C446 102 344 102 280 108Z",
        2: "M280 108C280 200 280 340 280 468C280 500 280 530 280 548C283 617 311 654 357 669C371 674 384 677 395 677C362 637 354 569 360 445C342 300 304 190 280 108Z",
        3: "M304 146C301 230 299 330 298 421C296 522 302 603 337 636",
        4: "M486 138C489 232 491 332 492 428C494 526 485 603 458 640",
        5: "M468 175C476 270 479 369 480 482",
        6: "M280 108C280 200 280 340 280 468C280 500 280 530 280 548C283 617 311 654 357 669C381 677 409 677 433 669C479 654 507 617 510 548C510 530 510 500 510 468C510 340 510 200 510 108C446 102 344 102 280 108Z",
        7: "M315 132C315 220 315 345 315 468C315 498 315 524 314 539C318 599 339 629 371 642C386 648 403 648 419 642C451 629 472 599 476 539C475 524 475 498 475 468C475 345 475 220 475 132C430 126 360 126 315 132Z",
        8: "M395 102C393 242 391 480 395 677",
        9: "M280 390Q394 354 510 390M280 501Q394 462 510 501",
        10: "M329 638Q395 595 461 638",
        11: "M510 307H584",
        12: "M461 638H646v18",
        13: "M318 89H472",
        14: "M674 462Q713 447 752 462",
        15: "M686 462Q713 453 740 462",
      },
      texts: {
        0: { value: "STRUCTURED FOR STRENGTH" },
        1: { value: "CLEAN FREE EDGE" },
        2: { value: "PRECISE FIT" },
        3: { value: "CUTICLE CURVE" },
        4: { value: "CLEAN PARALLELS" },
        5: { value: "LIGNE ARCHITECTURALE" },
        6: { value: "SIGNATURE 03. SQUARE" },
        7: { value: "STRUCTURED FOR STRENGTH", y: 57 },
        8: { value: "SIGNATURE 03. SQUARE · CLEAN FREE EDGE", y: 76 },
      },
      nodes: {
        0: { cx: 510, cy: 307 },
        1: { cx: 461, cy: 638 },
        2: { cx: 510, cy: 307 },
        3: { cx: 461, cy: 638 },
      },
    });

    // Round crown: continuous tangents at the crown and lateral joins.
    const roundState = extendState(almondState, {
      paths: {
        0: "M345 112C316 137 288.942 194 276 285C257 376 246 464 250 548C255 617 290 654 348 669C378 677 412 677 442 669C500 654 535 617 540 548C544 464 533 376 514 285C501.058 194 474 137 445 112C416 87 374 87 345 112Z",
        1: "M345 112C316 137 288.942 194 276 285C257 376 246 464 250 548C255 617 290 654 348 669C378 677 412 677 442 669C500 654 535 617 540 548C544 464 533 376 514 285C501.058 194 474 137 445 112C416 87 374 87 345 112Z",
        2: "M345 112C316 137 288.942 194 276 285C257 376 246 464 250 548C255 617 290 654 348 669C365 674 381 677 395 677C354 637 344 569 354 445C365 300 358 184 345 112Z",
        3: "M340 149C317 216 300 312 288 421C278 522 287 603 326 636",
        4: "M449 137C474 216 491 316 501 428C509 526 496 603 464 640",
        5: "M443 176C471 268 483 369 485 482",
        6: "M345 112C316 137 288.942 194 276 285C257 376 246 464 250 548C255 617 290 654 348 669C378 677 412 677 442 669C500 654 535 617 540 548C544 464 533 376 514 285C501.058 194 474 137 445 112C416 87 374 87 345 112Z",
        7: "M356 137C332 156 317.241 212 307 295C294 378 289 464 294 539C299 599 325 629 365 642C384 648 405 648 425 642C465 629 491 599 496 539C501 464 496 378 483 295C472.759 212 458 156 434 137C410 118 380 118 356 137Z",
        8: "M395 88C392 238 390 480 395 677",
        9: "M276 390Q394 350 514 390M259 501Q394 458 537 496",
        11: "M487 307H584",
        13: "M318 93H472",
        14: "M665 462Q713 410 761 462",
        15: "M677 462Q713 428 749 462",
      },
      texts: {
        0: { value: "ROUNDED FOR SOFTNESS" },
        1: { value: "CONTINUOUS CURVE" },
        2: { value: "PRECISE FIT" },
        3: { value: "CUTICLE CURVE" },
        4: { value: "GALBE NATUREL" },
        5: { value: "SOMMET ARRONDI" },
        6: { value: "SIGNATURE 01. ROUND" },
        7: { value: "ROUNDED FOR SOFTNESS", y: 61 },
        8: { value: "SIGNATURE 01. ROUND · CONTINUOUS CURVE", y: 80 },
      },
      nodes: {
        0: { cx: 487, cy: 307 },
        2: { cx: 487, cy: 307 },
      },
    });

    // Apply the same profile adjustment to silhouette, clip and wire paths.
    // These first paths contain absolute M/C/Q coordinate pairs only.
    const refineProfile = (state, scaleAtY) => {
      state.paths = state.paths.map((path, index) => {
        if (index > 10) return path;
        return path.replace(/([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)/g, (_, x, y) =>
          `${formatNumber(395 + (Number(x) - 395) * scaleAtY(Number(y)))} ${y}`);
      });
    };
    // Round: reduce the lower flare so the lateral edges are more parallel.
    refineProfile(roundState, (y) => 1.125 * (1 - 0.20 * Math.min(1, Math.max(0, (y - 220) / 200))));
    // Almond: rounded cap with tangents aligned to both side curves.
    // Keep clip, volume, outline and inset consistent during morphing.
    almondState.paths = almondState.paths.map((path, index) => {
      if (![0, 1, 2, 6, 7].includes(index)) return path;
      return path
        .replaceAll('M372 96C335 122', 'M365 102C335 122')
        .replaceAll('455 122 418 96C402.667 80 387.333 80 372 96Z', '455 122 425 102C405 88.667 385 88.667 365 102Z')
        .replaceAll('373 164 372 96Z', '373 164 365 102Z')
        .replaceAll('M383 119C357 146', 'M378 124C357 146')
        .replaceAll('433 146 407 119C399 109.667 391 109.667 383 119Z', '433 146 412 124C400 111.429 390 111.429 378 124Z');
    });

    // Match the visual widths while preserving each shape's contour.
    refineProfile(almondState, () => 0.90);
    refineProfile(squareState, () => 1.08);

    // Stiletto: preserve its proportions, reduce by 8%, and keep the cuticle baseline.
    stilettoState.paths = stilettoState.paths.map((path, index) => {
      if (index > 10) return path;
      return path.replace(/([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)/g, (_, x, y) =>
        `${formatNumber(395 + (Number(x) - 395) * 0.92)} ${formatNumber(677 + (Number(y) - 677) * 0.92)}`);
    });
    const shapeStates = {
      almond: almondState,
      stiletto: stilettoState,
      coffin: coffinState,
      square: squareState,
      round: roundState,
    };

    const annotationPairs = {
      'SCULPTED FOR BALANCE': "SCULPTÉE POUR L'ÉQUILIBRE",
      'STRUCTURED APEX': 'APEX STRUCTURÉ',
      'SHARPENED FOR IMPACT': "AFFÛTÉE POUR L'IMPACT",
      'STRUCTURED TIP': 'POINTE STRUCTURÉE',
      'REFINED FOR ELEGANCE': "AFFINÉE POUR L'ÉLÉGANCE",
      'TAPERED SIDES': 'PAROIS CONVERGENTES',
      'REFINED LINES': 'LIGNES AFFINÉES',
      'STRUCTURED FOR STRENGTH': 'STRUCTURÉE POUR LA FORCE',
      'CLEAN FREE EDGE': 'BORD LIBRE NET',
      'CLEAN PARALLELS': 'PARALLÈLES NETS',
      'ROUNDED FOR SOFTNESS': 'ARRONDIE POUR LA DOUCEUR',
      'CONTINUOUS CURVE': 'COURBE CONTINUE',
      'PRECISE FIT': 'AJUSTEMENT PRÉCIS',
      'SECURE FIT': 'MAINTIEN PRÉCIS',
      'CUTICLE CURVE': 'COURBE DE CUTICULE',
      'STRAIGHT FREE EDGE': 'BORD LIBRE DROIT',
      'ARCHITECTURAL LINE': 'LIGNE ARCHITECTURALE',
      'NATURAL CURVE': 'GALBE NATUREL',
      'ROUNDED APEX': 'SOMMET ARRONDI',
    };
    const localizeAnnotation = (value) => {
      if (!value) return value;
      let result = value;
      for (const [en, fr] of Object.entries(annotationPairs)) {
        result = result.replaceAll(isFrench ? en : fr, isFrench ? fr : en);
      }
      return result;
    };
    Object.values(shapeStates).forEach(state => {
      state.texts.forEach(item => { item.value = localizeAnnotation(item.value); });
    });

    const shapeMetaEn = {
      almond: {
        number: "02", name: "Almond", story: "Refinement embodied. Slender, delicate, elegant.",
        mobile: { title: "SCULPTED FOR BALANCE", detail: "SIGNATURE 02. ALMOND · STRUCTURED APEX", callout: "PRECISE FIT", calloutDetail: "CUTICLE CURVE" },
      },
      stiletto: {
        number: "05", name: "Stiletto", story: "Elegance with an edge. Sharp, daring, striking.",
        mobile: { title: "SHARPENED FOR IMPACT", detail: "SIGNATURE 05. STILETTO · STRUCTURED TIP", callout: "SECURE FIT", calloutDetail: "CUTICLE CURVE" },
      },
      coffin: {
        number: "04", name: "Coffin", story: "Boldness refined. Elongated, graphic, iconic.",
        mobile: { title: "REFINED FOR ELEGANCE", detail: "SIGNATURE 04. COFFIN · TAPERED SIDES", callout: "PRECISE FIT", calloutDetail: "CUTICLE CURVE" },
      },
      square: {
        number: "03", name: "Square", story: "Boldly defined. Clean, graphic, confident.",
        mobile: { title: "STRUCTURED FOR STRENGTH", detail: "SIGNATURE 03. SQUARE · CLEAN FREE EDGE", callout: "PRECISE FIT", calloutDetail: "CUTICLE CURVE" },
      },
      round: {
        number: "01", name: "Round", story: "Natural beauty refined. Soft, balanced, timeless.",
        mobile: { title: "ROUNDED FOR SOFTNESS", detail: "SIGNATURE 01. ROUND · CONTINUOUS CURVE", callout: "PRECISE FIT", calloutDetail: "CUTICLE CURVE" },
      },
    };

    const shapeMetaFr = {
      almond: { number: '02', name: 'Almond', story: 'La finesse incarnée. Élancée, délicate, élégante.', mobile: { title: "SCULPTÉE POUR L'ÉQUILIBRE", detail: 'SIGNATURE 02. ALMOND · APEX STRUCTURÉ', callout: 'AJUSTEMENT PRÉCIS', calloutDetail: 'COURBE DE CUTICULE' } },
      stiletto: { number: '05', name: 'Stiletto', story: 'L’élégance létale. Affûtée, provocante, redoutable.', mobile: { title: "AFFÛTÉE POUR L'IMPACT", detail: 'SIGNATURE 05. STILETTO · POINTE STRUCTURÉE', callout: 'MAINTIEN PRÉCIS', calloutDetail: 'COURBE DE CUTICULE' } },
      coffin: { number: '04', name: 'Coffin', story: 'L’audace maîtrisée. Élancée, graphique, iconique.', mobile: { title: "AFFINÉE POUR L'ÉLÉGANCE", detail: 'SIGNATURE 04. COFFIN · PAROIS CONVERGENTES', callout: 'AJUSTEMENT PRÉCIS', calloutDetail: 'COURBE DE CUTICULE' } },
      square: { number: '03', name: 'Square', story: 'Carrément assumée. Nette, graphique, affirmée.', mobile: { title: 'STRUCTURÉE POUR LA FORCE', detail: 'SIGNATURE 03. SQUARE · BORD LIBRE NET', callout: 'AJUSTEMENT PRÉCIS', calloutDetail: 'COURBE DE CUTICULE' } },
      round: { number: '01', name: 'Round', story: 'Le naturel sublimé. Douce, harmonieuse, intemporelle.', mobile: { title: 'ARRONDIE POUR LA DOUCEUR', detail: 'SIGNATURE 01. ROUND · COURBE CONTINUE', callout: 'AJUSTEMENT PRÉCIS', calloutDetail: 'COURBE DE CUTICULE' } },
    };
    const shapeMeta = isFrench ? shapeMetaFr : shapeMetaEn;

    const applyCopy = (shape) => {
      sourceTexts.forEach((node, index) => {
        node.textContent = shapeStates[shape].texts[index]?.value || "";
      });

      const mobileCopy = shapeMeta[shape].mobile;
      const mobileFields = {
        "[data-mobile-infography-title]": shapeMeta[shape].story,
        "[data-mobile-infography-detail]": mobileCopy.detail,
        "[data-mobile-infography-callout]": mobileCopy.callout,
        "[data-mobile-infography-callout-detail]": mobileCopy.calloutDetail,
      };
      Object.entries(mobileFields).forEach(([selector, value]) => {
        const node = almondArt.querySelector(selector);
        if (node) node.textContent = value;
      });

      const meta = shapeMeta[shape];
      almondArt.setAttribute("aria-label", isFrench ? `Étude technique animée d’une capsule ${meta.name.toLowerCase()}` : `Animated technical study of a ${meta.name.toLowerCase()} nail tip`);
      const title = almondArt.querySelector("title");
      const description = almondArt.querySelector("desc");
      if (title) title.textContent = isFrench ? `Capsule ${meta.name.toLowerCase()} - étude de forme` : `${meta.name} nail tip - shape study`;
      if (description) description.textContent = isFrench ? `Capsule ${meta.name.toLowerCase()} semi-transparente. ${meta.story}` : `Translucent ${meta.name.toLowerCase()} nail tip. ${meta.story}`;
      if (window.innerWidth <= 749) onScroll();
    };

    const createTransition = (fromState, toState) => ({
      paths: sourcePaths.map((path, index) => {
        const from = tokenizePath(fromState.paths[index] || "");
        const to = tokenizePath(toState.paths[index] || "");
        return {
          path, from, to,
          compatible: from.length === to.length && from.every((token, tokenIndex) => (
            isCommand(token) ? token === to[tokenIndex] : !isCommand(to[tokenIndex])
          )),
          fromValue: fromState.paths[index] || "",
          toValue: toState.paths[index] || "",
        };
      }),
      texts: sourceTexts.map((node, index) => ({ node, from: fromState.texts[index], to: toState.texts[index] })),
      nodes: sourceNodes.map((node, index) => ({ node, from: fromState.nodes[index], to: toState.nodes[index] })),
    });

    const renderTransition = (transition, progress) => {
      transition.paths.forEach(({ path, from, to, compatible, fromValue, toValue }) => {
        if (!compatible) {
          path.setAttribute("d", progress < 0.5 ? fromValue : toValue);
          return;
        }
        const value = from.map((token, index) => {
          if (isCommand(token)) return token;
          return formatNumber(Number(token) + (Number(to[index]) - Number(token)) * progress);
        }).join(" ");
        path.setAttribute("d", value);
      });

      transition.texts.forEach(({ node, from, to }) => {
        ["x", "y"].forEach((attribute) => {
          if (Number.isFinite(from?.[attribute]) && Number.isFinite(to?.[attribute])) {
            node.setAttribute(attribute, formatNumber(from[attribute] + (to[attribute] - from[attribute]) * progress));
          }
        });
      });

      transition.nodes.forEach(({ node, from, to }) => {
        ["cx", "cy", "r"].forEach((attribute) => {
          if (Number.isFinite(from?.[attribute]) && Number.isFinite(to?.[attribute])) {
            node.setAttribute(attribute, formatNumber(from[attribute] + (to[attribute] - from[attribute]) * progress));
          }
        });
      });
    };

    const playInfographyEntrance = () => {
      window.clearTimeout(infographyTimer);
      almondArt.classList.remove("is-infography-entering");
      void almondArt.offsetWidth;
      almondArt.classList.add("is-infography-entering");
      infographyTimer = window.setTimeout(() => {
        almondArt.classList.remove("is-infography-entering");
      }, 900);
    };

    const setDepth = () => {
      currentX += (targetX - currentX) * 0.085;
      currentY += (targetY - currentY) * 0.085;

      Object.values(stages).forEach((stage) => {
        stage.style.setProperty("--gj-pointer-x", currentX.toFixed(3));
        stage.style.setProperty("--gj-pointer-y", currentY.toFixed(3));
      });

      if (Math.abs(targetX - currentX) > 0.002 || Math.abs(targetY - currentY) > 0.002) {
        frame = requestAnimationFrame(setDepth);
      } else {
        frame = 0;
      }
    };

    const onPointerMove = (event) => {
      if (root.dataset.griffeParallax === "false" || reduceMotion.matches || window.innerWidth <= 760) return;
      targetX = Math.max(-1, Math.min(1, (event.clientX / window.innerWidth - 0.5) * 2));
      targetY = Math.max(-1, Math.min(1, (event.clientY / window.innerHeight - 0.5) * 2));
      if (!frame) frame = requestAnimationFrame(setDepth);
    };

    const applyStory = (shape) => {
      const nextStory = shapeMeta[shape];
      if (!nextStory || !story) return;

      if (storyIndex) storyIndex.textContent = `${nextStory.number} - ${nextStory.name}`;
      if (storyCopy) storyCopy.textContent = nextStory.story;
    };

    const updateStory = (shape, animate = true) => {
      if (!story || !animate || reduceMotion.matches) {
        applyStory(shape);
        return;
      }

      window.clearTimeout(storyTimer);
      story.classList.add("is-changing");
      storyTimer = window.setTimeout(() => {
        applyStory(shape);
        story.classList.remove("is-changing");
      }, 620);
    };

    const updateMenu = (shape, animateStory = true) => {
      buttons.forEach((button) => {
        const selected = button.dataset.shapeTarget === shape;
        button.classList.toggle("is-active", selected);
        button.setAttribute("aria-pressed", String(selected));
      });
      root.dataset.activeShape = shape;
      if (status) {
        const meta = shapeMeta[shape];
        status.textContent = isFrench ? `Forme ${meta.number}. ${meta.name} affichée` : `Shape ${meta.number}. ${meta.name} displayed`;
      }
      updateStory(shape, animateStory);
    };

    const clamp = (value, minimum = 0, maximum = 1) => Math.max(minimum, Math.min(maximum, value));
    const smoothstep = (value) => {
      const clamped = clamp(value);
      return clamped * clamped * (3 - 2 * clamped);
    };

    const renderGalleryScroll = () => {
      scrollFrame = 0;
      const rootRect = root.getBoundingClientRect();
      const travel = Math.max(1, root.offsetHeight - stages.almond.offsetHeight);
      const progress = clamp(-rootRect.top / travel) * 0.23;
      const compact = window.innerWidth <= 900;
      const copyProgress = smoothstep(progress / 0.14);
      const artProgress = compact
        ? smoothstep(clamp(progress / 0.23))
        : smoothstep(clamp((progress - 0.02) / 0.21));
      const galleryProgress = smoothstep(clamp((progress - 0.1) / 0.13));
      const cueProgress = smoothstep(progress / 0.07);
      const desktopShift = -Math.min(window.innerWidth * 0.145, 226);
      const artShift = compact ? 0 : desktopShift * artProgress;
      const artY = compact ? 0 : 18 * artProgress;
      const artScale = 1 + (compact ? 0 : 0.1) * artProgress;

      if (compact) {
        if (window.innerWidth <= 749) {
          // Fit the intro drawing between the real translated copy and the
          // shapes cue. Fixed percentages overlap the CTA on shorter phones.
          const heroCopy = stages.almond.querySelector(".gj-hero__copy");
          const heroInner = stages.almond.querySelector(".gj-hero__inner");
          const cue = stages.almond.querySelector(".gj-scroll-cue");
          const heroHeight = heroInner.clientHeight;
          const artStart = heroCopy.offsetTop + heroCopy.offsetHeight + 20;
          const artEnd = heroHeight - (cue?.offsetHeight || 44) - 42;
          const introHeight = Math.max(0, artEnd - artStart);
          const introWidth = Math.min(window.innerWidth, 540, introHeight * 1.08);
          const galleryHeading = stages.almond.querySelector('.gj-gallery-heading');
          const galleryMenu = stages.almond.querySelector('.gj-shape-menu');
          const caption = almondArt.querySelector('.gj-mobile-infography__heading');
          const captionHeight = caption?.offsetHeight || 48;
          const galleryStart = (galleryHeading ? galleryHeading.offsetTop + galleryHeading.offsetHeight : 150) + 24 + captionHeight + 18;
          const galleryEnd = (galleryMenu ? galleryMenu.offsetTop : heroHeight - 156) - 24;
          const galleryHeight = Math.max(0, galleryEnd - galleryStart);
          const galleryWidth = Math.min(window.innerWidth, 540, galleryHeight * 1.08);
          const galleryCenter = galleryStart + galleryWidth / 1.08 / 2;
          root.style.setProperty('--gj-mobile-caption-height', `${captionHeight}px`);
          const introCenter = artStart + introWidth / 1.08 / 2;
          const artCenter = introCenter + (galleryCenter - introCenter) * artProgress;
          const artWidth = introWidth + (galleryWidth - introWidth) * artProgress;
          root.style.setProperty("--gj-mobile-art-top", `${artCenter.toFixed(2)}px`);
          root.style.setProperty("--gj-mobile-art-width", `${artWidth.toFixed(2)}px`);
        } else {
          root.style.setProperty("--gj-mobile-art-top", `${(73 - 23 * artProgress).toFixed(2)}%`);
          root.style.removeProperty("--gj-mobile-art-width");
          root.style.removeProperty("--gj-mobile-caption-height");
        }
      } else {
        root.style.removeProperty("--gj-mobile-art-top");
        root.style.removeProperty("--gj-mobile-art-width");
        root.style.removeProperty("--gj-mobile-caption-height");
      }

      root.style.setProperty("--gj-copy-opacity", (1 - copyProgress).toFixed(3));
      root.style.setProperty("--gj-copy-blur", `${(copyProgress * 6).toFixed(2)}px`);
      root.style.setProperty("--gj-copy-x", `${((compact ? 0 : -56) * copyProgress).toFixed(2)}px`);
      root.style.setProperty("--gj-art-x", `${artShift.toFixed(2)}px`);
      root.style.setProperty("--gj-art-y", `${artY.toFixed(2)}px`);
      root.style.setProperty("--gj-art-scale", artScale.toFixed(4));
      root.style.setProperty("--gj-gallery-opacity", galleryProgress.toFixed(3));
      root.style.setProperty("--gj-gallery-y", `${((1 - galleryProgress) * 22).toFixed(2)}px`);
      root.style.setProperty("--gj-scroll-cue-opacity", (1 - cueProgress).toFixed(3));
      const galleryThreshold = 0.08;
      const viewportCenter = window.innerHeight * 0.5;
      const isSectionActive = rootRect.top <= viewportCenter && rootRect.bottom > viewportCenter;
      root.classList.toggle("is-gallery-mode", progress > galleryThreshold);
      root.classList.toggle("is-section-active", isSectionActive);
      root.querySelectorAll(".gj-gallery-direction").forEach((link) => {
        const isAvailable = isSectionActive && progress > galleryThreshold;
        link.setAttribute("aria-hidden", String(!isAvailable));
        link.tabIndex = isAvailable ? 0 : -1;
      });

    };

    const onScroll = () => {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(renderGalleryScroll);
    };

    const animateWindowScroll = (targetY, hash, requestedDuration = 1650) => {
      window.cancelAnimationFrame(scrollAnimationFrame);

      const startY = window.scrollY;
      const distance = targetY - startY;
      const duration = reduceMotion.matches ? 0 : requestedDuration;

      if (duration === 0 || Math.abs(distance) < 2) {
        window.scrollTo({ top: targetY, left: 0, behavior: "instant" });
        window.history.replaceState(null, "", hash);
        return;
      }

      const startedAt = performance.now();
      const animateScroll = (now) => {
        const linear = clamp((now - startedAt) / duration);
        const eased = 0.35 * linear + 2.3 * linear * linear - 1.65 * linear * linear * linear;
        window.scrollTo({ top: startY + distance * eased, left: 0, behavior: "instant" });
        if (scrollFrame) {
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = 0;
        }
        renderGalleryScroll();

        if (linear < 1) {
          scrollAnimationFrame = requestAnimationFrame(animateScroll);
          return;
        }

        scrollAnimationFrame = 0;
        window.history.replaceState(null, "", hash);
      };

      scrollAnimationFrame = requestAnimationFrame(animateScroll);
    };

    const scrollToGallery = (event) => {
      event.preventDefault();
      const anchor = root.querySelector(".gj-gallery-anchor");
      const targetY = Math.min(
        window.scrollY + anchor.getBoundingClientRect().top,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      animateWindowScroll(targetY, "#collection", 650);
    };

    const interruptShapeSwitch = () => {
      const wasSwitching = switching;
      morphGeneration += 1;
      window.clearTimeout(morphCopyTimer);
      morphCopyTimer = 0;
      queuedShape = null;
      queuedSequential = false;
      switching = false;
      morphTargetShape = null;
      if (wasSwitching) activeShape = "";
      almondArt.style.removeProperty("--gj-morph-pulse");
      almondArt.classList.remove("is-morphing", "is-label-swapping", "is-infography-exiting");
    };

    const scrollToHero = (event) => {
      event.preventDefault();
      interruptShapeSwitch();
      requestShape("round");
      animateWindowScroll(root.offsetTop, "#gj-stage-almond", 1450);
    };

    const reveal = (stage) => {
      stage.classList.add("is-shape-entering");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          stage.classList.add("is-ready");
          window.setTimeout(() => stage.classList.remove("is-shape-entering"), reduceMotion.matches ? 0 : 760);
        });
      });
    };

    const switchShape = (nextShape) => {
      if (switching || nextShape === activeShape || !shapeStates[nextShape]) return;
      switching = true;
      morphTargetShape = nextShape;
      const activeMorphGeneration = ++morphGeneration;
      updateMenu(nextShape);
      window.clearTimeout(infographyTimer);
      almondArt.classList.remove("is-infography-entering", "is-infography-exiting");

      const transition = createTransition(captureState(almondArt), shapeStates[nextShape]);
      const duration = reduceMotion.matches ? 0 : 750;
      const completeSwitch = () => {
        activeShape = nextShape;
        switching = false;
        morphTargetShape = null;

        if (!queuedShape || queuedShape === activeShape) {
          queuedShape = null;
          queuedSequential = false;
          return;
        }

        const pendingShape = queuedShape;
        const pendingSequential = queuedSequential;
        queuedShape = null;
        queuedSequential = false;
        requestShape(pendingShape, pendingSequential);
      };

      almondArt.classList.add("has-morphed", "is-morphing", "is-infography-exiting");

      if (duration === 0) {
        renderTransition(transition, 1);
        applyCopy(nextShape);
        almondArt.classList.remove("is-morphing", "is-label-swapping", "is-infography-exiting");
        completeSwitch();
        return;
      }

      morphCopyTimer = window.setTimeout(() => {
        if (activeMorphGeneration !== morphGeneration) return;
        morphCopyTimer = 0;
        almondArt.classList.add("is-label-swapping");
        applyCopy(nextShape);
        almondArt.classList.remove("is-infography-exiting");
        playInfographyEntrance();
      }, 300);

      const startedAt = performance.now();
      const animate = (now) => {
        if (activeMorphGeneration !== morphGeneration) return;
        const linear = Math.min(1, (now - startedAt) / duration);
        const eased = 0.5 - Math.cos(Math.PI * linear) / 2;
        const organic = Math.max(0, Math.min(1, eased + Math.sin(Math.PI * linear) * 0.018));
        renderTransition(transition, organic);
        almondArt.style.setProperty("--gj-morph-pulse", Math.sin(Math.PI * linear).toFixed(3));

        if (linear < 1) {
          requestAnimationFrame(animate);
          return;
        }

        renderTransition(transition, 1);
        almondArt.style.removeProperty("--gj-morph-pulse");
        almondArt.classList.remove("is-morphing", "is-label-swapping", "is-infography-exiting");
        completeSwitch();
      };

      requestAnimationFrame(animate);
    };

    const requestShape = (nextShape, sequential = false) => {
      if (!shapeStates[nextShape]) return;

      if (switching) {
        if (nextShape === morphTargetShape) return;
        if (!sequential) {
          interruptShapeSwitch();
        } else {
          queuedShape = nextShape;
          queuedSequential = sequential;
          return;
        }
      }

      if (nextShape === activeShape) return;

      if (!sequential) {
        switchShape(nextShape);
        return;
      }

      const activeIndex = shapeOrder.indexOf(activeShape);
      const targetIndex = shapeOrder.indexOf(nextShape);
      if (activeIndex < 0 || targetIndex < 0) return;

      const stepIndex = activeIndex + Math.sign(targetIndex - activeIndex);
      const stepShape = shapeOrder[stepIndex];
      if (!stepShape) return;

      if (stepShape !== nextShape) {
        queuedShape = nextShape;
        queuedSequential = true;
      }
      switchShape(stepShape);
    };

    // One carousel drives the shared hero/forms artwork on every viewport.
    // Pause offscreen, in background tabs, for keyboard focus and reduced motion.
    const restartCarousel = () => {
      window.clearTimeout(carouselTimer);
      carouselTimer = 0;
      if (carouselDestroyed || !root.isConnected || !carouselVisible || document.hidden || reduceMotion.matches || root.querySelector(":focus-visible")) return;
      carouselTimer = window.setTimeout(() => {
        if (!switching) {
          requestShape(shapeOrder[(shapeOrder.indexOf(activeShape) + 1) % shapeOrder.length]);
        }
        restartCarousel();
      }, carouselDelay);
    };

    const selectShapeFromMenu = (nextShape) => {
      requestShape(nextShape);
      restartCarousel();
    };

    const carouselObserver = new IntersectionObserver(([entry]) => {
      carouselVisible = entry.isIntersecting;
      restartCarousel();
    });
    carouselObserver.observe(stages.almond);
    document.addEventListener("visibilitychange", restartCarousel);
    reduceMotion.addEventListener("change", restartCarousel);
    root.addEventListener("focusin", restartCarousel);
    root.addEventListener("focusout", () => queueMicrotask(restartCarousel));
    const destroyCarousel = (event) => {
      if (!event.target.contains(root)) return;
      carouselDestroyed = true;
      window.clearTimeout(carouselTimer);
      carouselObserver.disconnect();
      document.removeEventListener("visibilitychange", restartCarousel);
      reduceMotion.removeEventListener("change", restartCarousel);
      document.removeEventListener("shopify:section:unload", destroyCarousel);
    };
    document.addEventListener("shopify:section:unload", destroyCarousel);

    buttons.forEach((button) => {
      button.addEventListener("click", () => selectShapeFromMenu(button.dataset.shapeTarget));
    });

    root.querySelectorAll('a[href="#collection"]').forEach((link) => {
      link.addEventListener("click", scrollToGallery);
    });
    backToHero?.addEventListener("click", scrollToHero);
    root.addEventListener("griffe:show-forms", scrollToGallery);
    root.addEventListener("griffe:focus-shape", (event) => {
      const nextShape = event.detail?.shape;
      if (!shapeStates[nextShape]) return;
      interruptShapeSwitch();
      requestShape(nextShape);
    });

    document.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    // The heading font can change the measured mobile copy after first paint.
    document.fonts?.ready.then(onScroll);
    renderTransition(createTransition(captureState(almondArt), shapeStates[activeShape]), 1);
    applyCopy(activeShape);
    updateMenu(activeShape, false);
    renderGalleryScroll();
    reveal(stages.almond);
  }

  function boot(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initShapeExplorer(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initShapeExplorer);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => boot(event.target));
})();
